import { useState, useEffect, useCallback } from 'react';
import { AuthContext } from '@/utils/auth-utils';
import { onAuthStateChanged } from 'firebase/auth';
import type { IdTokenResult, User } from 'firebase/auth';
import { auth, database } from '@/firebase';
import { ref, get } from 'firebase/database';
import type { TUserSubscription, UserProfile } from '@/types/user.types';
import { getUserEntitlements } from '@/api-services/common.service';
import { trackInterviewProgress } from '@/api-services/digital-twin.service';
import type { TTrackInterviewProgress } from '@/types/digital-twin.types';
import { consoleError } from '@/utils/common-utils';
import { LoadingSpinner } from '@/components/common/loading-spinner';
import { ipdata, showLocalizedPrice } from '@/api-services/public-api';
import { pricesUSD } from '@/constants';

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [idTokenResult, setIdTokenResult] = useState<IdTokenResult | null>(null);
  const [userEntitlements, setUserEntitlements] = useState<TUserSubscription | null>(null);
  const [interviewProgress, setInterviewProgress] = useState<TTrackInterviewProgress | null>(null);
  const [userIPdata, setUserIPdata] = useState<any>(null);
  const [localPrices, setLocalPrices] = useState(pricesUSD);

  // some old users have their name in differnt parms
  const [userName, setUserName] = useState<string>('');

  const formatAllPrices = async (userIPdata: any) => {
    const entries = Object.entries(pricesUSD);
    const formattedEntries = await Promise.all(
      entries.map(async ([key, value]) => {
        const formatted = await showLocalizedPrice(userIPdata, value);
        return [key, formatted];
      })
    );

    const formattedPrices = Object.fromEntries(formattedEntries);
    setLocalPrices(formattedPrices);
  };

  const refreshUser = async () => {
    if (auth.currentUser) {
      const tokenResult = await auth.currentUser.getIdTokenResult(true);
      const profile = await fetchUserProfile(auth.currentUser.uid);
      const entitlements = await getUserEntitlements();
      setIdTokenResult(tokenResult);
      setUserProfile(profile);
      setUserEntitlements(entitlements);
      if (tokenResult.claims?.role === 'creator') {
        const interviewProgress = await trackInterviewProgress();
        setInterviewProgress(interviewProgress);
      }
    }
  };

  const fetchUserProfile = useCallback(async (userId: string) => {
    try {
      const userRef = ref(database, `users/${userId}`);
      const snapshot = await get(userRef);
      const profileData = snapshot.val();
      if (profileData) {
        profileData.profileImageUrl = `${import.meta.env.VITE_BACKEND_URL_OLD}/v1/profile_picture?user_id=${userId}`;
      }
      return profileData as UserProfile;
    } catch (error) {
      consoleError('Error fetching user profile:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      setLoading(true);
      setUser(firebaseUser);
      if (firebaseUser) {
        try {
          const [profile, entitlements, tokenResult, userIPdata] = await Promise.all([
            fetchUserProfile(firebaseUser.uid),
            getUserEntitlements(),
            firebaseUser.getIdTokenResult(),
            ipdata(),
          ]);
          const creator = tokenResult?.claims?.role === 'creator' || false;
          if (creator) {
            const interviewProgress = await trackInterviewProgress();
            setInterviewProgress(interviewProgress);
          }
          if (userIPdata) {
            formatAllPrices(userIPdata);
          } else {
            formatAllPrices(null);
          }
          setUserIPdata(userIPdata);
          setUserProfile(profile);
          setUserName(profile?.first_name || profile?.firstName || 'user');
          setUserEntitlements(entitlements);
          setIdTokenResult(tokenResult);
        } catch (error) {
          consoleError('Failed to pass AuthContext', error);
          setUserProfile(null);
          setUserEntitlements(null);
        }
      } else {
        setUserProfile(null);
        setUserEntitlements(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [fetchUserProfile]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        userProfile,
        userEntitlements,
        idTokenResult,
        userName,
        interviewProgress,
        userIPdata,
        localPrices,
        refreshUser,
      }}
    >
      {loading ? (
        <div className="flex items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
